import { useEffect, useState } from "react"
import styles from "./Ai.module.scss"
import clsx from "clsx"
import Markdown from "react-markdown"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import SaveAsIcon from "@mui/icons-material/SaveAs"
import PrintIcon from "@mui/icons-material/Print"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import TableViewIcon from "@mui/icons-material/TableView"
import "./Ai.module.scss"
import DynamicChart from "./DynamicD3Chart"
import sanitizeHtml from "sanitize-html"
import CustomChart from "./CustomChart"
import remarkGfm from "remark-gfm"
import rehypeRaw from "rehype-raw"
import CustomLineChartWrapper from "src/blitz/Charts/CustomLineChart/CustomLineChartWrapper"
import { exportElementToPdf } from "./html-to-pdf-converter"

const AiResponseRenderer = ({
  data,
  reportDetails,
  theme,
  pdfCheckboxHandler,
  onClickHandler
}) => {
  const [isExpanded, setIsExpanded] = useState(true)

  const [checked, setChecked] = useState(false)

  const [showCopiedMessage, setShowCopiedMessage] = useState(false)

  const [formattedContent, setFormattedContent] = useState([])

  const formatDifferentData = () => {
    try {
      let isHtml = true
      const newContents = []
      if (data?.componentType === "customChart") {
        newContents.push({
          type: "customChart",
          content: data.message,
          chartProps: data?.chartProps
        })
        setFormattedContent(newContents)
        return
      }
      if (data?.message?.includes("const data")) {
        newContents.push({
          type: "data",
          content: data?.message
        })
      } else {
        // if the response has chart, table, markdown all together
        let htmlContents = [...data?.message.matchAll(/```html([\s\S]*?)```/g)]

        if (htmlContents?.length === 0) {
          htmlContents = [
            ...data?.message.matchAll(/```javascript([\s\S]*?)```/g)
          ]
          isHtml = false
        }

        const allHtmlContents = htmlContents?.map((match) => match[1].trim())

        if (allHtmlContents?.length > 0) {
          let remainingContent = ""
          if (isHtml) {
            remainingContent = data?.message
              ?.replace(/```html([\s\S]*?)```/g, "")
              .trim()
          } else {
            remainingContent = data?.message
              ?.replace(/```javascript([\s\S]*?)```/g, "")
              .trim()
          }
          if (remainingContent && !data?.message?.includes("d3.")) {
            newContents.push({
              type: "markdown",
              content: remainingContent
            })
          }
          allHtmlContents.forEach((content) => {
            if (content.includes("d3.")) {
              newContents.push({
                type: "d3",
                content: content
              })
            } else if (content.includes("<table")) {
              newContents.push({
                type: "table",
                content: content
              })
            }
          })
        } else {
          if (data?.message?.includes("<html")) {
            newContents.push({
              type: "html",
              content: data?.message
            })
          } else {
            newContents.push({
              type: "markdown",
              content: data?.message
            })
          }
        }
      }
      setFormattedContent(newContents)
    } catch (e) {
      console.log(e)
    }
  }

  useEffect(() => {
    formatDifferentData()
  }, [data, isExpanded])

  const downloadPDFHandler = async () => {
    try {
      await exportElementToPdf({
        elementId: `${data?.id || ""}`,
        fileName: `${reportDetails?.equipmentName || ""}-${
          data?.reportTitle || ""
        }-ai-report.pdf`,
        documentTitle: `${reportDetails?.equipmentName || ""}-${
          data?.reportTitle || ""
        }-ai-report`
      })
    } catch (e) {
      console.log(e)
    }
  }

  const downloadAsCSV = (id) => {
    try {
      const containerElem = document.getElementById(id)
      const tableContent = containerElem?.getElementsByTagName("table")?.[0]
      if (containerElem && tableContent) {
        // Select rows from table_id
        // @ts-ignore
        const rows = tableContent.querySelectorAll(" tr")
        // Construct csv
        const csv = []
        for (let i = 0; i < rows.length; i++) {
          const row = [],
            cols = rows[i].querySelectorAll("td, th")
          for (let j = 0; j < cols.length; j++) {
            // Clean innertext to remove multiple spaces and jumpline (break csv)
            // @ts-ignore
            let data = cols[j].innerText
              .replace(/(\r\n|\n|\r)/gm, "")
              .replace(/(\s\s)/gm, " ")
            data = data.replace(/"/g, '""')
            // Push escaped string
            row.push('"' + data + '"')
          }
          csv.push(row.join(","))
        }
        const csv_string = csv.join("\n")
        // Download it
        const filename =
          data?.reportTitle + "_" + new Date().toLocaleDateString() + ".csv"
        const link = document.createElement("a")
        link.style.display = "none"
        link.setAttribute("target", "_blank")
        link.setAttribute(
          "href",
          "data:text/csv;charset=utf-8," + encodeURIComponent(csv_string)
        )
        link.setAttribute("download", filename)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    } catch (e) {
      console.log("error downloading CSV", e)
    }
  }

  return (
    <div
      id={data?.id || ""}
      className={clsx(
        styles.aimodalmessage,
        { [styles.usermessage]: data.actor === "user" },
        { [styles.darkbackground]: data.actor === "user" && theme === "dark" }
      )}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          justifyContent: "space-between",
          fontWeight: 800,
          fontSize: 17,
          paddingBottom: 10
        }}
        className="header"
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <input
            className="no-print"
            checked={checked}
            onChange={() => {
              setChecked((checked) => !checked)
              pdfCheckboxHandler(data?.id)
            }}
            type="checkbox"
          ></input>
          <div onClick={() => setIsExpanded(!isExpanded)}>
            <span className="no-print">
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}{" "}
            </span>{" "}
            {(data?.reportTitle && data?.reportTitle) || ""}{" "}
          </div>
        </div>

        <div
          style={{ display: "flex", position: "relative" }}
          className="no-print"
        >
          <span onClick={downloadPDFHandler} title="Download as pdf">
            <PictureAsPdfIcon />
          </span>
          <span title="Save as HTML">
            <SaveAsIcon />
          </span>
          <span title="Print">
            <PrintIcon />
          </span>
          {showCopiedMessage && (
            <span
              style={{
                position: "absolute",
                background: "black",
                color: "white",
                fontWeight: 800,
                padding: 5,
                borderRadius: 5,
                fontSize: 11,
                right: -50,
                top: -25
              }}
            >
              Copied..!
            </span>
          )}
          <span
            title="Copy to clipboard"
            onClick={() => {
              navigator.clipboard.writeText(data?.message)
              setShowCopiedMessage(true)
              setTimeout(() => {
                setShowCopiedMessage(false)
              }, 2000)
            }}
          >
            <ContentCopyIcon />
          </span>
          {data?.message?.includes("<table>") && (
            <span
              title="CSV Download"
              onClick={() => {
                downloadAsCSV(data?.id)
              }}
            >
              <TableViewIcon />
            </span>
          )}
        </div>
      </div>
      <div
        className="no-print"
        style={{
          borderBottom: "1px solid darkgray",
          marginTop: -5,
          marginBottom: 10
        }}
      ></div>
      {/* {isExpanded ? data?.message?.includes('d3.select') ? <DynamicChart isPreview={false} llmResponse={data?.message} id={data?.id} /> : (data?.message?.includes('<html>') || data?.message?.includes('<ul>') || data?.message?.includes('<p>') || data?.message?.includes('<b>') || data?.message?.includes('<table>') || data?.message?.includes('<div')) ? <div className="content" dangerouslySetInnerHTML={{ __html: sanitizeHtml(data?.message) }}></div> : <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{data?.message}</Markdown> : null
        } */}
      {isExpanded
        ? formattedContent?.map((content, index) => {
            if (content?.type === "d3") {
              return (
                <DynamicChart
                  key={index}
                  isPreview={false}
                  llmResponse={content?.content}
                  id={data?.id}
                />
              )
            } else if (content?.type === "data") {
              return (
                <CustomChart
                  equipmentType={reportDetails?.equipment_type}
                  key={index}
                  isPreview={false}
                  llmResponse={content?.content}
                  id={data?.id}
                />
              )
            } else if (content?.type === "table" || content?.type === "html") {
              return (
                <div
                  key={index}
                  style={{ overflowX: "auto", width: "100%" }}
                  className="table-content"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(content?.content)
                  }}
                ></div>
              )
            } else if (content?.type === "markdown") {
              return (
                <div key={index} onClick={onClickHandler}>
                  <Markdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                  >
                    {content?.content}
                  </Markdown>
                </div>
              )
            } else if (content?.type === "customChart") {
              return (
                <div
                  key={index}
                  style={{
                    width: "80%",
                    height: 550,
                    background: "black",
                    color: "white"
                  }}
                >
                  <CustomLineChartWrapper {...content.chartProps} />
                </div>
              )
            }
          })
        : null}
    </div>
  )
}

export default AiResponseRenderer
